import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Loader2, ImagePlus, Upload, ChefHat, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { apiClient, formatApiError } from "@/lib/api";
import { imgUrl } from "@/utils/format";
import IngredientsEditor from "@/components/IngredientsEditor";

const CATEGORIES = [
  { k: "guineen", l: "Guinéen" },
  { k: "africain", l: "Africain" },
  { k: "grillades", l: "Grillades" },
  { k: "fastfood", l: "Fast-food" },
  { k: "boissons", l: "Boissons" },
  { k: "desserts", l: "Desserts" },
  { k: "libanaise", l: "Libanaise" },
  { k: "asiatique", l: "Asiatique" },
  { k: "afrique_nord", l: "Afrique du Nord" },
  { k: "petitdej", l: "Petit-déj" },
];

/**
 * Add or edit a dish (with photo upload).
 * Props:
 *  - open: bool
 *  - dish: null | existing dish object (edit mode)
 *  - onClose: () => void
 *  - onSaved: (dish) => void  // called with fresh dish after save
 *  - onDeleted: (dishId) => void  // called after delete
 */
export default function DishFormModal({ open, dish, onClose, onSaved, onDeleted, initialCategory }) {
  const { t } = useTranslation();
  const isEdit = !!dish;
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [category, setCategory] = useState("guineen");
  const [available, setAvailable] = useState(true);
  const [spicy, setSpicy] = useState(0);
  const [image, setImage] = useState("");
  const [optionGroups, setOptionGroups] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [cooking, setCooking] = useState({ choices: [], default: "" });
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileRef = useRef(null);

  // Reset when opening/switching
  useEffect(() => {
    if (!open) return;
    if (dish) {
      setName(dish.name || "");
      setDescription(dish.description || "");
      setPrice(String(dish.price ?? ""));
      setCategory(dish.category || "guineen");
      setAvailable(dish.available !== false);
      setSpicy(Number(dish.spicy_level) || 0);
      setImage(dish.image || "");
      setIngredients(Array.isArray(dish.ingredients) ? dish.ingredients : []);
      setCooking({ choices: dish.cooking_choices || [], default: dish.cooking_default || "" });
    } else {
      setName(""); setDescription(""); setPrice("");
      setCategory(initialCategory || "guineen"); setAvailable(true); setSpicy(0); setImage("");
      setIngredients([]);
      setCooking({ choices: [], default: "" });
    }
  }, [open, dish, initialCategory]);

  if (!open) return null;

  const pickFile = () => fileRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error(t("rdash.dfImgTooBig"));
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await apiClient.post("/uploads/image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImage(data.url);
      toast.success(t("rdash.dfPhotoImported"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const generateAI = async () => {
    if (!name.trim()) return toast.error(t("rdash.dfNameFirst"));
    setGenerating(true);
    try {
      const { data } = await apiClient.post("/uploads/generate-dish-image", {
        name: name.trim(),
        description: description.trim(),
      });
      setImage(data.url);
      toast.success(t("rdash.dfAIok"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setGenerating(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error(t("rdash.dfNameRequired"));
    const p = Number(price);
    if (!Number.isFinite(p) || p <= 0) return toast.error(t("rdash.dfPriceInvalid"));
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        price: p,
        category,
        available,
        spicy_level: spicy,
        image: image || null,
        ingredients: ingredients.filter((i) => (i.name || "").trim()),
        cooking_choices: cooking.choices.filter((c) => (c || "").trim()),
        cooking_default: cooking.default,
        option_groups: [],
      };
      let data;
      if (isEdit) {
        const res = await apiClient.patch(`/my/restaurant/dishes/${dish.dish_id}`, payload);
        data = res.data;
        toast.success(t("rdash.dfUpdated"));
      } else {
        const res = await apiClient.post("/my/restaurant/dishes", payload);
        data = res.data;
        toast.success(t("rdash.dfAdded"));
      }
      onSaved?.(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!isEdit) return;
    if (!window.confirm(`Supprimer "${dish.name}" du menu ?`)) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/my/restaurant/dishes/${dish.dish_id}`);
      toast.success(t("rdash.dfDeleted"));
      onDeleted?.(dish.dish_id);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 backdrop-blur-sm p-4" data-testid="dish-form-modal">
      <form onSubmit={submit} className="bg-white rounded-2xl w-full max-w-2xl p-6 relative max-h-[92vh] overflow-y-auto">
        <button type="button" onClick={onClose} data-testid="dish-modal-close" className="absolute right-3 top-3 rounded-full p-1.5 hover:bg-elevated" aria-label={t("rdash.dfClose")}>
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-2 mb-1">
          <ChefHat className="w-5 h-5 text-brand-red" />
          <h3 className="font-display text-xl font-semibold">{isEdit ? t("rdash.dfEditTitle") : t("rdash.dfAddTitle")}</h3>
        </div>
        <p className="text-sm text-mute mb-5">{t("rdash.dfPhotoTip")}</p>

        {/* Image uploader */}
        <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-5 mb-5">
          <div>
            <div
              className={`relative aspect-square rounded-2xl overflow-hidden border-2 border-dashed ${image ? "border-transparent" : "border-sand hover:border-brand-red"} bg-elevated grid place-items-center cursor-pointer`}
              onClick={pickFile}
              data-testid="dish-image-drop"
            >
              {image ? (
                <>
                  <img src={imgUrl(image)} alt={t("rdash.dfPreview")} className="w-full h-full object-cover" />
                  {(uploading || generating) && (
                    <div className="absolute inset-0 bg-black/40 grid place-items-center">
                      <Loader2 className="w-6 h-6 text-white animate-spin" />
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center px-4">
                  {(uploading || generating) ? (
                    <>
                      <Loader2 className="w-6 h-6 mx-auto animate-spin text-brand-red" />
                      {generating && <div className="text-[10px] text-brand-red mt-1">{t("rdash.dfNano")}</div>}
                    </>
                  ) : (
                    <>
                      <ImagePlus className="w-8 h-8 mx-auto text-mute" />
                      <div className="text-xs text-mute mt-2">{t("rdash.dfClickImport")}<br />{t("rdash.dfOr")} <b>{t("rdash.dfGenAI")}</b><br />{t("rdash.dfImportHint")}</div>
                    </>
                  )}
                </div>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="dish-image-input" />
            <button
              type="button"
              onClick={pickFile}
              disabled={uploading || generating}
              data-testid="dish-image-btn"
              className="mt-2 w-full rounded-full border border-sand hover:border-brand-red text-sm font-semibold py-2 inline-flex items-center justify-center gap-1.5"
            >
              <Upload className="w-3.5 h-3.5" /> {image ? t("rdash.dfChangePhoto") : t("rdash.dfImportPhoto")}
            </button>
            <button
              type="button"
              onClick={generateAI}
              disabled={generating || uploading || !name.trim()}
              data-testid="dish-ai-generate"
              title={!name.trim() ? t("rdash.dfNameFirst") : t("rdash.dfGenAITitle")}
              className="mt-2 w-full rounded-full text-white bg-brand-red hover:bg-[#C81F2D] text-sm font-semibold py-2 inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
            >
              {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {generating ? t("rdash.generating") : image ? t("rdash.dfRegenBtn") : t("rdash.dfGenAIBtn")}
            </button>
          </div>

          <div className="space-y-3">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-mute block mb-1">{t("rdash.dfName")}</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={80}
                data-testid="dish-name"
                className="w-full bg-white border border-sand rounded-xl px-3 py-2.5 text-sm outline-none focus:border-brand-red"
                placeholder={t("rdash.dfNamePlaceholder")}
              />
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-mute block mb-1">{t("rdash.dfPrice")}</span>
                <input
                  value={price}
                  onChange={(e) => setPrice(e.target.value.replace(/[^0-9]/g, ""))}
                  required
                  inputMode="numeric"
                  data-testid="dish-price"
                  className="w-full bg-white border border-sand rounded-xl px-3 py-2.5 text-sm outline-none focus:border-brand-red"
                  placeholder="45000"
                />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-mute block mb-1">{t("rdash.dfCategory")}</span>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  data-testid="dish-category"
                  className="w-full bg-white border border-sand rounded-xl px-3 py-2.5 text-sm outline-none focus:border-brand-red"
                >
                  {CATEGORIES.map((c) => <option key={c.k} value={c.k}>{t(`rdash.cat_${c.k}`)}</option>)}
                </select>
              </label>
            </div>

            <label className="block">
              <span className="text-xs uppercase tracking-widest text-mute block mb-1">{t("rdash.dfDesc")}</span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                maxLength={280}
                data-testid="dish-description"
                className="w-full bg-white border border-sand rounded-xl px-3 py-2 text-sm outline-none focus:border-brand-red"
                placeholder={t("rdash.dfDescPlaceholder")}
              />
              <span className="text-[10px] text-mute float-right mt-1">{description.length}/280</span>
            </label>

            <div className="block">
              <span className="text-xs uppercase tracking-widest text-mute block mb-1">{t("rdash.dfSpicy")}</span>
              <div className="flex gap-2" data-testid="dish-spicy-group">
                {[{ v: 0, l: t("rdash.dfNotSpicy"), e: "🌶️" }, { v: 1, l: t("rdash.dfSpicyMid"), e: "🌶️" }, { v: 2, l: t("rdash.dfVerySpicy"), e: "🌶️🌶️" }].map((o) => (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setSpicy(o.v)}
                    data-testid={`dish-spicy-${o.v}`}
                    className={`flex-1 rounded-xl border px-2 py-2 text-xs font-semibold transition-colors ${spicy === o.v ? "bg-brand-red text-white border-brand-red" : "bg-white text-ink border-sand hover:border-brand-red"}`}
                  >
                    <span className={o.v === 0 ? "grayscale opacity-50" : ""}>{o.e}</span> {o.l}
                  </button>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={available}
                onChange={(e) => setAvailable(e.target.checked)}
                data-testid="dish-available"
                className="w-4 h-4"
              />
              <span className="text-sm">{t("rdash.dfAvailable")}</span>
            </label>
          </div>
        </div>

        {/* Composition du plat (ingrédients + cuisson) */}
        <div className="mb-5 pt-4 border-t border-sand">
          <IngredientsEditor ingredients={ingredients} onChange={setIngredients} cooking={cooking} onCookingChange={setCooking} />
        </div>

        <div className="flex gap-2 items-center pt-3 border-t border-sand">
          {isEdit && (
            <button
              type="button"
              onClick={remove}
              disabled={deleting || saving}
              data-testid="dish-delete-btn"
              className="rounded-full border border-brand-red/40 text-brand-red hover:bg-brand-red hover:text-white transition-colors px-4 py-2 text-sm font-semibold inline-flex items-center gap-1.5"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              {t("rdash.dfDelete")}
            </button>
          )}
          <div className="flex-1" />
          <button type="button" onClick={onClose} className="rounded-full border border-sand hover:border-ink px-5 py-2 text-sm font-semibold">
            {t("rdash.dfCancel")}
          </button>
          <button
            type="submit"
            disabled={saving || uploading || generating}
            data-testid="dish-save-btn"
            className="rounded-full bg-brand-green hover:bg-[#007A4A] disabled:opacity-60 text-white px-5 py-2 text-sm font-semibold inline-flex items-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEdit ? t("rdash.dfSave") : t("rdash.dfAddMenu")}
          </button>
        </div>
      </form>
    </div>
  );
}
